import { QueryKeys } from "@/constants/Query";
import { useApi } from "@/hooks/useApi";
import axiosInstance from "@/lib/axios";
import { ServiceRequest } from "@curai/js-fhir-tools";

export enum LabTestWorkflowStatus {
  COMPLETED = "completed",
  NEEDS_MANUAL_RESULTING = "needs_manual_resulting",
  ORDERED = "ordered",
  FAILED = "failed",
  NEEDS_SUBMISSION = "needs_submission",
  PENDING = "pending",
  NEEDS_TRIAGE = "needs_triage",
  NEEDS_TRIAGE_PARTIAL = "needs_triage_partial",
  NEEDS_DISCUSSION = "needs_discussion",
}

export interface LabTestGroupItem {
  testCode: string;
  workflowStatus: LabTestWorkflowStatus;
  serviceRequest: ServiceRequest;
}

export interface FailedLabTestGroupItem extends LabTestGroupItem {
  canRetry: boolean;
  needsRefundIfCancelled: boolean;
}

interface LabTestsResponse {
  completed: LabTestGroupItem[];
  needsManualResulting: LabTestGroupItem[];
  ordered: LabTestGroupItem[];
  failed: FailedLabTestGroupItem[];
  pending: LabTestGroupItem[];
  needsDiscussion: LabTestGroupItem[];
  needsSubmission: LabTestGroupItem[];
  needsTriage: LabTestGroupItem[];
  needsTriagePartial: LabTestGroupItem[];
}

export const useLabTests = (patientId?: string) => {
  return useApi<LabTestsResponse>({
    queryOptions: {
      enabled: Boolean(patientId),
      queryKey: QueryKeys.Patient.MedicalRecords.Labs.DEFAULT(patientId),
      queryFn: async () => {
        const response = await axiosInstance.get("/labs/order/order-history", {
          params: { patientId },
        });
        return response?.data;
      },
      select: (data) => {
        return {
          completed: data.completed.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
          needsManualResulting: data.needsManualResulting.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
          ordered: data.ordered.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
          failed: data.failed.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
          needsSubmission: data.needsSubmission.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
          pending: data.pending.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
          needsTriage: data.needsTriage.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
          needsTriagePartial: data.needsTriagePartial.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
          needsDiscussion: data.needsDiscussion.map((item) => ({
            ...item,
            serviceRequest: new ServiceRequest(item.serviceRequest),
          })),
        };
      },
    },
  });
};
